import {addDoc, collection} from 'firebase/firestore';
import {useState} from 'react';
import {env} from '../../constants';
import {firebaseAuth, firebaseFirestore} from '../../setup/firebase';
import {extractSheetIdFromUrl} from './extractSheetIdFromUrl';
import {
  generationJobProjectFirebaseSchema,
  generationJobProjectInputSchema,
  type GenerationJobProject,
  type GenerationJobProjectInput,
} from './schema';
import {resolveOutputFolderId} from './resolveOutputFolderId';

export interface UseCreateGenerationJobProjectOptions {
  onCompleted?: (data: GenerationJobProject) => void;
  onError?: (error: Error) => void;
}

export default function useCreateGenerationJobProject(options?: UseCreateGenerationJobProjectOptions) {
  const [loading, setLoading] = useState(false);

  const createProject = async (input: GenerationJobProjectInput) => {
    const user = firebaseAuth.currentUser;
    if (!user) {
      const err = new Error('Not authenticated');
      options?.onError?.(err);
      return;
    }
    setLoading(true);
    try {
      const parsed = generationJobProjectInputSchema.parse(input);
      const sheetId = extractSheetIdFromUrl(parsed.sheetUrlOrId);
      const outputFolderId = resolveOutputFolderId(parsed.outputFolderUrlOrId);
      const sourceImageFileId = parsed.sourceImageFileId?.trim();
      const sourceImageUrl = parsed.sourceImageUrl?.trim();
      const collRef = collection(firebaseFirestore, 'user', user.uid, env.collection.generationJobProject);
      const systemPrompt = parsed.systemPrompt?.trim();
      const data = {
        ...(parsed.name != null && parsed.name !== '' && {name: parsed.name}),
        gdriveTokenId: parsed.gdriveTokenId,
        ...(sourceImageFileId && {sourceImageFileId}),
        ...(sourceImageUrl && {sourceImageUrl}),
        sheetId,
        outputFolderId,
        creationTimestamp: Math.floor(Date.now() / 1000),
        ...(systemPrompt && {systemPrompt}),
        ...(parsed.edit !== undefined && {edit: parsed.edit}),
        ...(parsed.outputQuality && {outputQuality: parsed.outputQuality}),
        ...(parsed.outputShape && {outputShape: parsed.outputShape}),
      };
      generationJobProjectFirebaseSchema.parse(data);
      const docRef = await addDoc(collRef, data);
      const result: GenerationJobProject = {
        id: docRef.id,
        name: parsed.name ?? undefined,
        gdriveTokenId: parsed.gdriveTokenId,
        sourceImageFileId: sourceImageFileId ?? undefined,
        sourceImageUrl: sourceImageUrl ?? undefined,
        sheetId,
        outputFolderId,
        creationTimestamp: data.creationTimestamp,
        systemPrompt: systemPrompt ?? undefined,
        edit: parsed.edit,
        outputQuality: parsed.outputQuality,
        outputShape: parsed.outputShape,
      };
      options?.onCompleted?.(result);
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      options?.onError?.(err);
    } finally {
      setLoading(false);
    }
  };

  return [createProject, {loading}] as const;
}
